import { attach, combine, merge, sample } from 'effector';
import { useUnit } from 'effector-react/ssr';

import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { Lens } from 'monocle-ts';

import {
    CartAddProductsData,
    CartData,
    CartDetails,
    CartItem,
    CartItemId,
    CartSetQuantityProductData,
    PromoProducts,
} from '@domains/cart';

import { cartDomain } from '@stores/cart/domain';
import { errorNotificationFx } from '@stores/helpers';
import { getProfileFx, getUserData } from '@stores/profile';

import { ApiError } from '@api/client/errors';
import { apiCart, apiCartAddProducts, apiCartClear, apiCartSetQuantityProduct } from '@api/methods';
import { CartSetQuantityProductPayload } from '@api/protocol';

import { handleMarketingOnCartStateChange } from '@utils/analytics';
import { applyLensPayload } from '@utils/lens';

type CartDataState = CartData & {
    isInitialized: boolean;
    isLoading: boolean;
};

// --- Initial state ---
const cartInitialState: CartDataState = {
    isInitialized: false,
    isLoading: false,
    cart: {
        items: [],
        items_price_without_discounts: 0 as FeipApi.Kopecks,
        items_discount: 0 as FeipApi.Kopecks,
        items_price_with_discounts: 0 as FeipApi.Kopecks,
    },
    promo_products: [],
    disable_online_payment: true,
};

// --- Effects ---
const _fetchCartFx = cartDomain.createEffect<void, CartData, ApiError>().use(apiCart);
const _clearCartFx = cartDomain.createEffect<void, CartData, ApiError>().use(apiCartClear);
const _addProductsToCartFx = cartDomain
    .createEffect<CartAddProductsData, CartData, ApiError>()
    .use(apiCartAddProducts);
const _setQuantityProductFx = cartDomain
    .createEffect<CartSetQuantityProductData, CartData, ApiError>()
    .use(apiCartSetQuantityProduct);

// --- Attached effects ---
export const fetchCartFx = attach({ effect: _fetchCartFx });
export const clearCartFx = attach({ effect: _clearCartFx });
export const addProductsToCartFx = attach({ effect: _addProductsToCartFx });
export const setQuantityProductFx = attach({ effect: _setQuantityProductFx });

// --- Events ---
export const setCartDetails = cartDomain.createEvent<CartDetails>();
export const setIsInitialized = cartDomain.createEvent<CartDataState['isInitialized']>();
export const setIsLoading = cartDomain.createEvent<CartDataState['isLoading']>();
export const clearCart = cartDomain.createEvent();

// --- Lenses ---
const cartDetailsLens = Lens.fromProp<CartDataState>()('cart');
const isInitializedLens = Lens.fromProp<CartDataState>()('isInitialized');
const isLoadingLens = Lens.fromProp<CartDataState>()('isLoading');

// --- Stores ---
const $cart = cartDomain
    .createStore<CartDataState>(cartInitialState, { name: 'cart' })
    .on(setCartDetails, applyLensPayload(cartDetailsLens))
    .on(setIsInitialized, applyLensPayload(isInitializedLens))
    .on(setIsLoading, applyLensPayload(isLoadingLens));

export const $cartDetails = $cart.map<CartDetails>(({ cart }) => cart);
export const $cartDetailsItems = $cartDetails.map<CartItem[]>(({ items }) => items);
export const $cartPromoProducts = $cart.map<PromoProducts>(({ promo_products }) => promo_products);
export const $isCartLoading = $cart.map<boolean>(({ isLoading }) => isLoading);
export const $isCartInitialized = $cart.map<boolean>(({ isInitialized }) => isInitialized);
export const $totalCartProducts = $cartDetails.map<number>(({ items }) =>
    items.reduce((acc, cur) => acc + cur.quantity, 0),
);
export const $isOnlinePaymentDisabled = $cart.map(
    ({ disable_online_payment }) => disable_online_payment,
);

// --- Remove from cart ---
export const $unavailableOrderingProducts = $cartDetailsItems.map<CartItem[]>((items) =>
    items.filter(({ product }) => !product.is_possible_to_order),
);
const removeProductFromCartFx = attach({
    effect: _setQuantityProductFx,
    mapParams: (params: CartItemId): CartSetQuantityProductPayload => {
        return { item_id: params.item_id, quantity: 0 };
    },
});
const _removeProductsMultipleFx = cartDomain.createEffect<
    CartItemId[],
    Promise<PromiseSettledResult<CartData>[]>,
    ApiError
>((items) => Promise.allSettled(items.map(({ item_id }) => removeProductFromCartFx({ item_id }))));
export const removeProductsMultipleFx = attach({
    effect: _removeProductsMultipleFx,
    source: $unavailableOrderingProducts,
    mapParams: (_: void, source): CartItemId[] =>
        source.map((cartItem) => ({ item_id: cartItem.id })),
});

// --- Hooks ---
export const useFindCartItemByProductId = (productId: number): CartItem | null =>
    pipe(
        useUnit($cartDetailsItems),
        A.findFirst((item) => item.product.id === productId),
        O.toNullable,
    );

// --- Samples ---
const mergeEffectsDoneData = merge([
    fetchCartFx.doneData,
    clearCartFx.doneData,
    addProductsToCartFx.doneData,
    setQuantityProductFx.doneData,
    removeProductFromCartFx.doneData,
]);

const mergeEffectsFailData = merge([
    fetchCartFx.failData,
    clearCartFx.failData,
    addProductsToCartFx.failData,
    setQuantityProductFx.failData,
    removeProductFromCartFx.failData,
]);

const mergeEffectsToTriggerCartChange = merge([
    clearCartFx.doneData,
    addProductsToCartFx.doneData,
    setQuantityProductFx.doneData,
]);

const combineEffectsInFlight = combine([
    fetchCartFx.inFlight,
    clearCartFx.inFlight,
    addProductsToCartFx.inFlight,
    setQuantityProductFx.inFlight,
    removeProductFromCartFx.inFlight,
]);

// Note: Must be higher than the actual $cart update for the same events
sample({
    clock: mergeEffectsToTriggerCartChange,
    source: $cart,
    fn: (state, response) => {
        handleMarketingOnCartStateChange(response.cart.items, state.cart.items);
    },
});

sample({
    clock: mergeEffectsDoneData,
    source: { cart: $cart, isCartInitialized: $isCartInitialized },
    fn: ({ cart, isCartInitialized }, response) => {
        if (isCartInitialized) {
            return {
                ...cart,
                cart: response.cart,
                disable_online_payment: response.disable_online_payment,
            };
        }
        return {
            ...cart,
            cart: response.cart,
            promo_products: response.promo_products,
            disable_online_payment: response.disable_online_payment,
        };
    },
    target: $cart,
});

sample({
    clock: [getUserData, getProfileFx],
    target: fetchCartFx,
});

sample({
    clock: [fetchCartFx.failData, clearCartFx.failData],
    target: errorNotificationFx,
});

sample({
    clock: [mergeEffectsDoneData, mergeEffectsFailData],
    source: $isCartInitialized,
    filter: (isCartInitialized) => !isCartInitialized,
    fn: () => true,
    target: setIsInitialized,
});

sample({
    clock: combineEffectsInFlight,
    fn: (numInFlight) => numInFlight.some((inFlight) => inFlight > 0),
    target: setIsLoading,
});

sample({
    clock: removeProductsMultipleFx.finally,
    fn: () => undefined,
    target: fetchCartFx,
});

// Clear cart
sample({
    clock: clearCart,
    target: clearCartFx,
});
